// localStorage配置管理器实现

import type { LocalStorageManager } from './storage';
import type { ReadingProgress, ReadingSettings } from '@/types/reading';
import { DEFAULT_READING_SETTINGS } from './storage';

export class LocalStorageManagerImpl implements LocalStorageManager {
  private readonly PREFIX = 'reading_system_';
  private readonly SETTINGS_KEY = 'user_settings';
  private readonly READING_SETTINGS_KEY = 'reading_settings';
  private readonly PROGRESS_PREFIX = 'progress_';
  private readonly CONFIG_PREFIX = 'config_';

  // ============================================================================
  // 用户设置
  // ============================================================================

  saveUserSettings(settings: any): void {
    try {
      const key = this.getKey(this.SETTINGS_KEY);
      const data = {
        ...settings,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save user settings:', error);
      throw new Error('Failed to save user settings to localStorage');
    }
  }

  getUserSettings(): any {
    try {
      const key = this.getKey(this.SETTINGS_KEY);
      const data = localStorage.getItem(key);
      
      if (!data) {
        return this.getDefaultUserSettings();
      }

      const parsed = JSON.parse(data);
      return {
        ...this.getDefaultUserSettings(),
        ...parsed
      };
    } catch (error) {
      console.error('Failed to get user settings:', error);
      return this.getDefaultUserSettings();
    }
  }

  // ============================================================================
  // 阅读设置
  // ============================================================================

  saveReadingSettings(settings: ReadingSettings): void {
    try {
      const key = this.getKey(this.READING_SETTINGS_KEY);
      const data = {
        ...settings,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save reading settings:', error);
      throw new Error('Failed to save reading settings to localStorage');
    }
  }

  getReadingSettings(): ReadingSettings {
    try {
      const key = this.getKey(this.READING_SETTINGS_KEY);
      const data = localStorage.getItem(key);
      
      if (!data) {
        return DEFAULT_READING_SETTINGS;
      }

      const parsed = JSON.parse(data);
      return {
        ...DEFAULT_READING_SETTINGS,
        ...parsed
      };
    } catch (error) {
      console.error('Failed to get reading settings:', error);
      return DEFAULT_READING_SETTINGS;
    }
  }

  // ============================================================================
  // 阅读进度（快速访问）
  // ============================================================================

  saveReadingProgress(progress: ReadingProgress): void {
    try {
      const key = this.getKey(this.PROGRESS_PREFIX + progress.bookId);
      const data = {
        ...progress,
        lastReadTime: progress.lastReadTime.toISOString()
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save reading progress:', error);
      throw new Error('Failed to save reading progress to localStorage');
    }
  }

  getReadingProgress(bookId: string): ReadingProgress | null {
    try {
      const key = this.getKey(this.PROGRESS_PREFIX + bookId);
      const data = localStorage.getItem(key);
      
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);
      return {
        ...parsed,
        lastReadTime: new Date(parsed.lastReadTime)
      };
    } catch (error) {
      console.error('Failed to get reading progress:', error);
      return null;
    }
  }

  // ============================================================================
  // 简单配置数据
  // ============================================================================

  saveConfig(key: string, value: any): void {
    try {
      const storageKey = this.getKey(this.CONFIG_PREFIX + key);
      const data = {
        value,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.error(`Failed to save config ${key}:`, error);
      throw new Error(`Failed to save config ${key} to localStorage`);
    }
  }

  getConfig(key: string): any {
    try {
      const storageKey = this.getKey(this.CONFIG_PREFIX + key);
      const data = localStorage.getItem(storageKey);
      
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);
      return parsed.value;
    } catch (error) {
      console.error(`Failed to get config ${key}:`, error);
      return null;
    }
  }

  removeConfig(key: string): void {
    try {
      const storageKey = this.getKey(this.CONFIG_PREFIX + key);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Failed to remove config ${key}:`, error);
    }
  }

  // ============================================================================
  // 导入导出
  // ============================================================================

  exportData(): string {
    try {
      const exportData: any = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        data: {}
      };

      // 导出所有阅读系统相关的数据
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.PREFIX)) {
          const value = localStorage.getItem(key);
          if (value) {
            const cleanKey = key.substring(this.PREFIX.length);
            exportData.data[cleanKey] = JSON.parse(value);
          }
        }
      }

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Failed to export data:', error);
      throw new Error('Failed to export localStorage data');
    }
  }

  importData(data: string): boolean {
    try {
      const importData = JSON.parse(data);
      
      // 验证数据格式
      if (!importData.version || !importData.data) {
        throw new Error('Invalid import data format');
      }

      // 备份当前数据
      const backup = this.exportData();
      
      try {
        // 清除现有数据
        this.clearAllData();
        
        // 导入新数据
        for (const [key, value] of Object.entries(importData.data)) {
          const storageKey = this.getKey(key);
          localStorage.setItem(storageKey, JSON.stringify(value));
        }

        return true;
      } catch (importError) {
        // 如果导入失败，恢复备份
        console.error('Import failed, restoring backup:', importError);
        this.clearAllData();
        this.importData(backup);
        throw importError;
      }
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }

  // ============================================================================
  // 数据管理
  // ============================================================================

  clearAllData(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Failed to clear all data:', error);
      throw new Error('Failed to clear localStorage data');
    }
  }

  getStorageSize(): number {
    let totalSize = 0;
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.PREFIX)) {
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += key.length + value.length;
          }
        }
      }
    } catch (error) {
      console.error('Failed to calculate storage size:', error);
    }

    return totalSize;
  }

  getStorageInfo(): { used: number; available: number; quota: number; itemCount: number } {
    const quota = 10 * 1024 * 1024; // 假设10MB限制
    const used = this.getStorageSize();
    let itemCount = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.PREFIX)) {
          itemCount++;
        }
      }
    } catch (error) {
      console.error('Failed to get storage info:', error);
    }

    return {
      used,
      available: quota - used,
      quota,
      itemCount
    };
  }

  // ============================================================================
  // 数据迁移和版本管理
  // ============================================================================

  migrateData(fromVersion: string, toVersion: string): boolean {
    try {
      console.log(`Migrating localStorage data from ${fromVersion} to ${toVersion}`);
      
      // 这里可以添加具体的迁移逻辑
      // 目前只是一个占位符实现
      
      switch (fromVersion) {
        case '1.0':
          if (toVersion === '1.1') {
            return this.migrateFrom1_0To1_1();
          }
          break;
        default:
          console.warn(`No migration path from ${fromVersion} to ${toVersion}`);
          return false;
      }

      return true;
    } catch (error) {
      console.error('Data migration failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private getKey(key: string): string {
    return this.PREFIX + key;
  }

  private getDefaultUserSettings(): any {
    return {
      theme: 'auto',
      language: 'zh-CN',
      notifications: true,
      autoBackup: true,
      syncEnabled: false,
      createdAt: new Date().toISOString()
    };
  }

  private migrateFrom1_0To1_1(): boolean {
    try {
      // 示例迁移：添加新的默认设置
      const settings = this.getUserSettings();
      if (!settings.autoBackup) {
        settings.autoBackup = true;
        this.saveUserSettings(settings);
      }
      return true;
    } catch (error) {
      console.error('Migration from 1.0 to 1.1 failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 批量操作
  // ============================================================================

  getAllReadingProgress(): Record<string, ReadingProgress> {
    const progressMap: Record<string, ReadingProgress> = {};
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.getKey(this.PROGRESS_PREFIX))) {
          const bookId = key.substring(this.getKey(this.PROGRESS_PREFIX).length);
          const progress = this.getReadingProgress(bookId);
          if (progress) {
            progressMap[bookId] = progress;
          }
        }
      }
    } catch (error) {
      console.error('Failed to get all reading progress:', error);
    }

    return progressMap;
  }

  clearAllReadingProgress(): void {
    try {
      const keysToRemove: string[] = [];
      const progressPrefix = this.getKey(this.PROGRESS_PREFIX);
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(progressPrefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Failed to clear all reading progress:', error);
      throw new Error('Failed to clear reading progress from localStorage');
    }
  }

  getAllConfigs(): Record<string, any> {
    const configs: Record<string, any> = {};
    
    try {
      const configPrefix = this.getKey(this.CONFIG_PREFIX);
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(configPrefix)) {
          const configKey = key.substring(configPrefix.length);
          const value = this.getConfig(configKey);
          if (value !== null) {
            configs[configKey] = value;
          }
        }
      }
    } catch (error) {
      console.error('Failed to get all configs:', error);
    }

    return configs;
  }

  // ============================================================================
  // 健康检查和诊断
  // ============================================================================

  healthCheck(): { healthy: boolean; issues: string[] } {
    const issues: string[] = [];
    
    try {
      // 检查localStorage是否可用
      const testKey = this.getKey('health_check_test');
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (error) {
      issues.push('localStorage is not available');
    }

    // 检查存储空间
    const storageInfo = this.getStorageInfo();
    if (storageInfo.used / storageInfo.quota > 0.9) {
      issues.push('localStorage is nearly full (>90%)');
    }

    // 检查关键设置是否存在
    try {
      this.getUserSettings();
      this.getReadingSettings();
    } catch (error) {
      issues.push('Failed to load critical settings');
    }

    return {
      healthy: issues.length === 0,
      issues
    };
  }
}